import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  userReportByPortalsType,
  userReportType,
} from "../../../../../services/api/types/reports/reports";
import { formatKey, formatFilterRow } from "../../utils/helpers";

interface ExportToExcelParams {
  filters: Record<string, any>;
  users: userReportType[] | userReportByPortalsType[];
  reportName?: string;
  generatedBy?: string;
  time?: string;
  isGroupedByPortal: boolean;
  fileName: string;
}

export const exportToExcel = ({
  filters,
  users,
  reportName,
  generatedBy,
  time,
  isGroupedByPortal = false,
  fileName,
}: ExportToExcelParams) => {
  // Flatten the data structure if grouped by portal
  let flattenedUsers: userReportType[] = [];

  if (isGroupedByPortal) {
    const portalData = users as userReportByPortalsType[];
    portalData.forEach((portal) => {
      portal.users.forEach((user) => {
        flattenedUsers.push({
          portalUid: portal.portalUid || "",
          name: portal.name || "",
          userUid: user.userUid || "",
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          parentPortalUid: portal.parentPortalUid || "",
          userAttributes: user.userAttributes || {},
          registeredAt: user.registeredAt || "",
        });
      });
    });
  } else {
    flattenedUsers = users as userReportType[];
  }

  // Collect all unique attribute keys from all users
  const allAttributeKeys = new Set<string>();
  flattenedUsers.forEach((user) => {
    if (user.userAttributes) {
      Object.keys(user.userAttributes).forEach((key) =>
        allAttributeKeys.add(key)
      );
    }
  });

  const hasUserAttributes = allAttributeKeys.size > 0;

  // Sheet 1: Filters + Report metadata
  const filterSheetData: any[] = [
    ["Report type:", reportName || ""],
    ["Generated by:", generatedBy || ""],
    ["Time:", time || ""],
    ["Applied Filters"],
  ];

  const generalFilterRow = formatFilterRow([
    ["Portal", filters.general?.Portal],
    ["Group results by portal", filters.general?.GroupByPortal],
  ]);
  if (generalFilterRow.length > 0) {
    filterSheetData.push(generalFilterRow);
  }

  // Only add User Attributes section if there are user attributes with values
  if (
    hasUserAttributes &&
    filters.userAttributes &&
    Object.keys(filters.userAttributes).length > 0
  ) {
    const userAttributesRow = formatFilterRow(
      Object.entries(filters.userAttributes).map(
        ([key, value]) => [formatKey(key), value] as [string, unknown]
      )
    );
    if (userAttributesRow.length > 0) {
      filterSheetData.push(["User Attributes"]);
      filterSheetData.push(userAttributesRow);
    }
  }

  const assignmentRow1 = formatFilterRow([
    ["Assignment", filters.assignments?.Assignment],
    ["Assignment user status", filters.assignments?.AssignmentStatus],
    ["Progress status", filters.assignments?.ProgressStatus],
  ]);
  const assignmentRow2 = formatFilterRow([
    ["Deadline", filters.assignments?.DueDate],
    ["Start date", filters.assignments?.StartDate],
    ["Completion", filters.assignments?.EndDate],
    ["Assign date", filters.assignments?.CreatedAt],
  ]);
  if (assignmentRow1.length > 0 || assignmentRow2.length > 0) {
    filterSheetData.push(["Assignments"]);
    if (assignmentRow1.length > 0) filterSheetData.push(assignmentRow1);
    if (assignmentRow2.length > 0) filterSheetData.push(assignmentRow2);
  }
  const filterSheet = XLSX.utils.aoa_to_sheet(filterSheetData);

  // Sheet 2: User Table
  const userSheetData = flattenedUsers.map((u) => {
    const row: Record<string, any> = {
      PortalUid: u.portalUid || "",
      ParentPortalUid: u.parentPortalUid || "",
      "First Name": u.firstName || "",
      "Last Name": u.lastName || "",
      Email: u.email || "",
    };

    // Add all attributes only if there are any attributes
    if (hasUserAttributes) {
      allAttributeKeys.forEach((key) => {
        const value = u.userAttributes?.[key];
        row[key] =
          value === null || value === undefined || value === "" ? "" : value;
      });
    }

    row["Date Registered"] = u.registeredAt || "";

    return row;
  });

  const userSheet = XLSX.utils.json_to_sheet(userSheetData);

  // Create workbook & append both sheets
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, filterSheet, "Info");
  XLSX.utils.book_append_sheet(workbook, userSheet, "Data");

  // Export
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${fileName}.xlsx`);
};
